// ============================================================
// constants/sale.js — ค่าคงที่ + ป้ายแสดงผลกลางของงานขาย/เช่า (ฝั่งหน้าจอ admin)
// ------------------------------------------------------------
// ★ ค่าที่ "เก็บลง DB" ต้องตรงกับ CDS-P2-Backend/constants/sale.js ทุกตัวอักษร
//   หน้าจอเทียบสตริงตรง ๆ — เปลี่ยนค่าที่นี่โดยไม่เปลี่ยน backend = ตัวกรอง/ป้ายพังเงียบ
// ★ ป้าย (label) + สี ของสถานะเดียวกันต้องมาจากไฟล์นี้ที่เดียว
//   เดิมแต่ละหน้าเขียนเอง "เผยแพร่ / อนุมัติ (เผยแพร่) / เผยแพร่แล้ว" ปนกัน
//   และสี "ปิดงาน" เทาในโมดัลแต่เขียวในแดชบอร์ด — ผู้ใช้อ่านแล้วนึกว่าคนละสถานะ
// ============================================================

// ---------- ประเภทประกาศ (SaleListings.listing_type) ----------
export const LISTING_TYPE = {
  SALE: "ขาย",
  RENT: "เช่า",
  BOTH: "ขายและเช่า",
};
export const LISTING_TYPES = Object.values(LISTING_TYPE);

// ประกาศแบบไหนมีฝั่งขาย / ฝั่งเช่า
export const hasSale = (type) => type === LISTING_TYPE.SALE || type === LISTING_TYPE.BOTH;
export const hasRent = (type) => type === LISTING_TYPE.RENT || type === LISTING_TYPE.BOTH;

// ---------- สถานะห้อง ----------
export const SELLING_STATUS = ["ว่าง", "จอง", "ขายแล้ว", "ยกเลิก"];
export const RENT_STATUS = ["ว่าง", "จอง", "ให้เช่าแล้ว", "ยกเลิก"];

// ---------- สถานะการเผยแพร่ (SaleListings.publish_status) ----------
export const PUBLISH_STATUS = {
  PENDING: "รอเผยแพร่",
  PUBLISHED: "เผยแพร่",
  HIDDEN: "ยังไม่เผยแพร่",
};

// ★ ป้ายใช้คำเดียวกับค่าใน DB ตรง ๆ — ไม่มีคำแปลที่สอง ("รออนุมัติ" / "ยังไม่ดำเนินการ" เลิกใช้)
//   ปุ่มกระทำ (อนุมัติ / ไม่อนุมัติ) ยังเป็นคำกริยาได้ แต่ "ป้ายสถานะ" ต้องเป็นชุดนี้
export const PUBLISH_BADGE = {
  [PUBLISH_STATUS.PUBLISHED]: { label: PUBLISH_STATUS.PUBLISHED, tint: "#dcfce7", color: "#166534" },
  [PUBLISH_STATUS.PENDING]: { label: PUBLISH_STATUS.PENDING, tint: "#fff2d9", color: "#8a5a00" },
  [PUBLISH_STATUS.HIDDEN]: { label: PUBLISH_STATUS.HIDDEN, tint: "#e5e7eb", color: "#374151" },
};

// ห้องที่ยังไม่มีประกาศเลย (ไม่ใช่สถานะใน DB — ไม่มีแถว SaleListings)
export const NO_LISTING_BADGE = { label: "ยังไม่ลงประกาศ", tint: "#f1e8f8", color: "#6b21a8" };

// ★ ประกาศเก่าที่สร้างก่อนมีคอลัมน์ publish_status ยังไม่มีค่านี้ — เดาจาก is_published แทน
export const publishStatusOf = (listing) => {
  if (!listing) return "";
  if (listing.publish_status) return listing.publish_status;
  return listing.is_published ? PUBLISH_STATUS.PUBLISHED : PUBLISH_STATUS.HIDDEN;
};

// ---------- สถานะ "ขึ้นหน้าเว็บจริงไหม" (รวมช่วงวันประกาศ) ----------
// ★ เกณฑ์ต้องตรงกับ publishedWhere ของ CDS-P2-Backend/modules/sale-public/sale-public.repository.js
//   (is_published + announce_start_at <= ตอนนี้ + announce_end_at >= ตอนนี้ หรือเป็น null)
export const LIVE_STATE = {
  LIVE: "live", // ขึ้นเว็บจริงอยู่ตอนนี้
  WAITING: "waiting", // เผยแพร่แล้วแต่ยังไม่ถึงวันเริ่มประกาศ
  EXPIRED: "expired", // เผยแพร่แล้วแต่พ้นวันสิ้นสุดประกาศ
  PENDING: "pending", // รอผู้ดูแลยืนยัน
  HIDDEN: "hidden", // ยังไม่เผยแพร่
  NONE: "none", // ไม่มีประกาศ
};

// รับ "2026-10-25 00:00:00" / ISO / Date -> Date (ไม่ถูกต้อง = null)
const parseDateTime = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(String(value).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// จำนวนวัน (ตามปฏิทิน) จากวันนี้ถึงวันเริ่มประกาศ — 0 = เริ่มวันนี้ (แม้ยังไม่ถึงเวลา)
export const daysUntilStart = (listing, now = new Date()) => {
  const start = parseDateTime(listing?.announce_start_at);
  if (!start) return 0;
  return Math.max(0, Math.round((startOfDay(start) - startOfDay(now)) / 86400000));
};

export const liveStateOf = (listing, now = new Date()) => {
  if (!listing) return LIVE_STATE.NONE;
  const status = publishStatusOf(listing);
  if (status === PUBLISH_STATUS.PENDING) return LIVE_STATE.PENDING;
  if (status !== PUBLISH_STATUS.PUBLISHED || listing.is_published === false) return LIVE_STATE.HIDDEN;
  const start = parseDateTime(listing.announce_start_at);
  const end = parseDateTime(listing.announce_end_at);
  if (start && start > now) return LIVE_STATE.WAITING;
  if (end && end < now) return LIVE_STATE.EXPIRED;
  return LIVE_STATE.LIVE;
};

const WAITING_BADGE = { tint: "#dbeafe", color: "#1d4ed8" };
const EXPIRED_BADGE = { tint: "#fee2e2", color: "#991b1b" };

// ป้ายของประกาศ 1 ใบ (ไม่มีประกาศ = "ยังไม่ลงประกาศ")
// เผยแพร่แล้วแต่ยังไม่ขึ้นเว็บ จะแยกป้ายให้ชัด: รอถึงวันเริ่ม (บอกอีกกี่วัน) / หมดช่วงประกาศ
export const publishBadgeOf = (listing, now = new Date()) => {
  if (!listing) return NO_LISTING_BADGE;
  const state = liveStateOf(listing, now);
  if (state === LIVE_STATE.WAITING) {
    const days = daysUntilStart(listing, now);
    return { ...WAITING_BADGE, label: days > 0 ? `รอเริ่มประกาศ · อีก ${days} วัน` : "รอเริ่มประกาศ · เริ่มวันนี้" };
  }
  if (state === LIVE_STATE.EXPIRED) return { ...EXPIRED_BADGE, label: "หมดช่วงประกาศ" };
  return PUBLISH_BADGE[publishStatusOf(listing)] || PUBLISH_BADGE[PUBLISH_STATUS.HIDDEN];
};

// ---------- สถานะคำขอนัดดูห้อง (SaleInquiries.status) ----------
export const INQUIRY_STATUS = {
  NEW: "ใหม่",
  CONTACTED: "ติดต่อแล้ว",
  APPOINTED: "นัดแล้ว",
  CLOSED: "ปิดงาน",
};
export const INQUIRY_STATUS_LIST = Object.values(INQUIRY_STATUS);

// tone = สีหลัก (ตัวเลข/แท่งกราฟ) · pill = ป้ายทรงแคปซูล
// ★ "ปิดงาน" = เทา ทุกหน้า — งานจบแล้ว ไม่ต้องดึงสายตา (เดิมแดชบอร์ด/หน้านัดหมายเป็นเขียว)
export const INQUIRY_STATUS_META = {
  [INQUIRY_STATUS.NEW]: { tone: "#dc3545", pill: { background: "#dc3545", color: "#fff" } },
  [INQUIRY_STATUS.CONTACTED]: { tone: "#8a5a00", pill: { background: "#fff2d9", color: "#8a5a00" } },
  [INQUIRY_STATUS.APPOINTED]: { tone: "#1d4ed8", pill: { background: "#dbeafe", color: "#1d4ed8" } },
  [INQUIRY_STATUS.CLOSED]: { tone: "#6c757d", pill: { background: "#e5e7eb", color: "#374151" } },
};

// ป้ายสถานะคำขอ (ของเดิมที่หน้าอื่น import อยู่)
export const INQUIRY_STATUS_STYLE = Object.fromEntries(
  Object.entries(INQUIRY_STATUS_META).map(([key, meta]) => [key, meta.pill])
);
export const INQUIRY_STATUS_TONE = Object.fromEntries(
  Object.entries(INQUIRY_STATUS_META).map(([key, meta]) => [key, meta.tone])
);

// เหตุผลที่ปิดงาน — ตัวแรกต้องตรงกับ CLOSE_REASON_SUCCESS ฝั่ง backend (sale-inquiry.service.js)
//   เลือกตัวนี้ = ปิดดีลได้จริง backend เปลี่ยนสถานะห้องเป็น ขายแล้ว/ให้เช่าแล้ว ให้
export const CLOSE_REASON_SUCCESS = "ปิดการขาย/ปล่อยเช่าได้";
export const CLOSE_REASONS = [CLOSE_REASON_SUCCESS, "ผู้สนใจไม่สนใจแล้ว", "ติดต่อไม่ได้", "ห้องไม่ว่างแล้ว"];

// ---------- helper แสดงผล ----------
export const money = (value) => Number(value || 0).toLocaleString("th-TH");

// "YYYY-MM-DDTHH:mm" (DateTimePicker) -> "YYYY-MM-DD HH:mm" (API)
export const toApiDateTime = (value) => (value ? String(value).replace("T", " ") : null);

// วันที่ปัจจุบันตามเวลาเครื่อง (ไม่ใช่ UTC) — "YYYY-MM-DD"
// ★ toISOString() เป็น UTC ช่วง 00:00–06:59 ของไทยจะได้วันที่ของเมื่อวาน
export const localDateKey = (date = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};
